import { useEffect } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";

type Props = {
  message: string;
  onHide: () => void;
  duration?: number;
  /** Optional link back to whatever the action produced, e.g. the saved
   * draft or the collection a blogger just landed in — lets the user
   * confirm the action actually took effect, not just that a request fired. */
  action?: { label: string; onClick: () => void };
  /** Raise the toast above a fixed/sticky bottom bar (e.g. the wizard's
   * footer nav) so the two don't overlap. Default clears nothing extra. */
  bottomOffsetPx?: number;
  /** "error" is red and lingers longer — used for the global mutation-error
   * toast (App.tsx QueryClient defaultOptions.mutations.onError), where the
   * message is often something the user needs a beat to actually read
   * (e.g. "Недостаточно средств: нужно 41000₽, доступно 12000₽"). */
  variant?: "success" | "error";
};

export function Toast({ message, onHide, duration, action, bottomOffsetPx = 24, variant = "success" }: Props) {
  const isError = variant === "error";
  const effectiveDuration = duration ?? (isError ? 6000 : action ? 4500 : 2500);
  useEffect(() => {
    const t = setTimeout(onHide, effectiveDuration);
    return () => clearTimeout(t);
  }, [onHide, effectiveDuration]);

  return (
    <div
      className="fixed right-6 z-[200] flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg fade-up"
      style={{ background: isError ? "#4A1414" : "#1C1C1C", color: "#fff", minWidth: 220, bottom: bottomOffsetPx }}
    >
      {isError
        ? <AlertCircle size={16} style={{ color: "#FF8A80", flexShrink: 0 }} />
        : <CheckCircle2 size={16} style={{ color: "#AADB54", flexShrink: 0 }} />}
      <span className="text-[13.5px] font-display font-semibold flex-1">{message}</span>
      {action && (
        <button
          onClick={() => { action.onClick(); onHide(); }}
          className="text-[12.5px] font-display font-bold flex-shrink-0 hover:underline"
          style={{ color: "#AADB54" }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
